import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiHelpCircle } = FiIcons;

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const mainFaqs = [
    {
      question: "Is this just another template website?",
      answer: "No. It is custom written and structured for trade services and conversion. It is designed to earn trust and drive enquiries."
    },
    {
      question: "How do I get started?",
      answer: "Simply submit the enquiry form below with your business details. We will review your information and contact you with a custom landing page plan within 1-2 business days."
    },
    {
      question: "What if I already have a website?",
      answer: "This can replace your homepage or run alongside your current site as your main enquiry engine."
    },
    {
      question: "How long does it take?",
      answer: "You receive the first preview within 3 business days after your plan is approved."
    },
    {
      question: "What if I do not like the first version?",
      answer: "You can request changes. We refine the page before launch."
    }
  ];

  const hesitationFaqs = [
    {
      question: "What if I need to think about it?",
      answer: "That is normal. But this is not a blind leap. You preview your page before it goes live and you are protected by our first-revision guarantee. Thinking about it only delays the same outcome."
    },
    {
      question: "What if this is not right for my business?",
      answer: "That is why we build from your details and show you a full preview. If the first revision is not aligned with what was agreed, you can walk away with a full refund."
    },
    {
      question: "What if I change my mind after submitting?",
      answer: "If we have not started, there is no obligation. If we have built and the first revision is not aligned, you are still covered. You are never trapped."
    },
    {
      question: "I have been burned by marketing before.",
      answer: "Most marketing sells promises. We sell a tangible asset you can see before launch."
    },
    {
      question: "Why not wait?",
      answer: "Waiting does not improve your position. Competitors who look more professional keep winning trust every week you delay."
    }
  ];

  const AccordionItem = ({ faq, index, offset = 0 }) => {
    const actualIndex = index + offset;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        viewport={{ once: true }}
        className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden mb-4"
      >
        <button
          onClick={() => setOpenIndex(openIndex === actualIndex ? -1 : actualIndex)}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
        >
          <span className="text-white font-semibold pr-4">{faq.question}</span>
          <SafeIcon
            icon={FiChevronDown}
            className={`w-5 h-5 text-white/60 transition-transform duration-200 ${openIndex === actualIndex ? 'rotate-180' : ''}`}
          />
        </button>
        <AnimatePresence>
          {openIndex === actualIndex && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-4">
                <p className="text-white/80 leading-relaxed">{faq.answer}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <section id="faq" className="py-20 bg-gradient-to-br from-gray-900 to-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-white/80">
            Everything you need to know before making your enquiry.
          </p>
        </motion.div>

        <div className="mb-20">
          {mainFaqs.map((faq, index) => (
            <AccordionItem key={index} faq={faq} index={index} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-4 py-2 rounded-full mb-6">
            <SafeIcon icon={FiHelpCircle} className="text-blue-400" />
            <span className="text-blue-200 text-xs font-bold uppercase tracking-widest">Still Unsure?</span>
          </div>
          <h3 className="text-2xl font-bold text-white mb-8">We understand the hesitation.</h3>
        </motion.div>

        <div>
          {hesitationFaqs.map((faq, index) => (
            <AccordionItem key={index} faq={faq} index={index} offset={mainFaqs.length} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;